import { useState, useEffect } from "react";
import { Header } from "../components/Header";
import { Footer } from "../components/Footer";
import { StepProgress } from "../components/StepProgress";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  ArrowRight,
  Sparkles,
  Building2,
  Info,
  Lock,
} from "lucide-react";
import { useNavigate } from "react-router-dom";

const INPUT_CLS =
  "rounded-sm h-11 text-sm border-[#D1D5DB] text-[#1F2937] placeholder:text-[#9CA3AF] focus:border-[#025979] focus:ring-[#025979] transition-colors";

const STORAGE_KEY = "primes4_rfp_client_info";

const RFP_STEPS = ["Client Information", "Document Upload", "Review & Submit"];

const EMPTY_FORM = {
  company_name: "",
  contact_name: "",
  role: "",
  email: "",
  phone: "",
  industry: "",
  country: "",
  notes: "",
};

function isValidEmail(value) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());
}

export default function EvaluateRfpClientInfoPage() {
  const navigate = useNavigate();
  const [form, setForm] = useState(EMPTY_FORM);
  const [errors, setErrors] = useState({});

  // Restore saved draft (if any) so users coming back from upload step keep their info.
  useEffect(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) {
        const saved = JSON.parse(raw);
        setForm((prev) => ({ ...prev, ...saved }));
      }
    } catch (_e) {
      /* ignore */
    }
  }, []);

  const updateField = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    if (errors[key]) {
      setErrors((prev) => {
        const next = { ...prev };
        delete next[key];
        return next;
      });
    }
  };

  const validate = () => {
    const next = {};
    if (!form.company_name.trim()) next.company_name = "Company name is required";
    if (!form.contact_name.trim()) next.contact_name = "Contact name is required";
    if (!form.email.trim()) {
      next.email = "Email is required";
    } else if (!isValidEmail(form.email)) {
      next.email = "Enter a valid email address";
    }
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleContinue = () => {
    if (!validate()) return;
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(form));
    } catch (_e) {
      /* ignore storage failures */
    }
    navigate("/evaluate/rfp-intake");
  };

  return (
    <div
      data-testid="rfp-client-info-page"
      className="min-h-screen flex flex-col bg-[#F8FAFC]"
    >
      <Header />

      <StepProgress currentStep={1} total={3} labels={RFP_STEPS} />

      <main className="flex-1 pt-8 sm:pt-10 pb-12">
        <div className="w-full max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Page header */}
          <div className="mb-7">
            <div className="inline-flex items-center gap-1.5 bg-[#E0F2FE] border border-[#025979]/15 rounded-sm px-3 py-1.5 mb-2">
              <Sparkles
                className="w-3.5 h-3.5 text-[#025979]"
                strokeWidth={1.5}
              />
              <span className="text-xs font-medium text-[#025979]">
                Step 1 — Client Information
              </span>
            </div>
            <p
              data-testid="rfp-client-info-step-indicator"
              className="text-[11px] text-[#9CA3AF] mb-4 tracking-wide"
            >
              Step 1 of 3 • Takes ~1 minute
            </p>
            <h1
              data-testid="rfp-client-info-title"
              className="text-xl sm:text-2xl font-bold tracking-tight text-[#1F2937]"
              style={{ fontFamily: "Chivo, sans-serif" }}
            >
              RFP &amp; Requirements Analysis
            </h1>
            <p
              data-testid="rfp-client-info-subtitle"
              className="mt-2 text-[13px] text-[#4B5563] leading-relaxed max-w-2xl"
            >
              Capture client and opportunity context before uploading RFP,
              scope, or requirements documents for SmartBid analysis.
            </p>
          </div>

          {/* Main form card */}
          <div className="bg-white border border-[#E5E7EB] rounded-sm shadow-sm">
            <div className="p-6 sm:p-8">
              <div className="flex items-center gap-2 mb-5">
                <Building2
                  className="w-4 h-4 text-[#025979]"
                  strokeWidth={1.5}
                />
                <h2
                  className="text-sm font-semibold text-[#1F2937] uppercase tracking-wider"
                  style={{ fontFamily: "Chivo, sans-serif" }}
                >
                  Tell us about your organization
                </h2>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-5 gap-y-5">
                <div className="sm:col-span-2">
                  <Label
                    htmlFor="rfp-company-name"
                    className="text-xs font-medium text-[#1F2937] mb-1.5 block"
                  >
                    Company Name <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="rfp-company-name"
                    data-testid="rfp-field-company-name"
                    placeholder="e.g. Acme Logistics GmbH"
                    value={form.company_name}
                    onChange={(e) => updateField("company_name", e.target.value)}
                    className={`${INPUT_CLS} ${errors.company_name ? "border-red-400" : ""}`}
                  />
                  {errors.company_name && (
                    <p
                      data-testid="rfp-error-company-name"
                      className="text-xs text-red-500 mt-1.5"
                    >
                      {errors.company_name}
                    </p>
                  )}
                </div>

                <div>
                  <Label
                    htmlFor="rfp-contact-name"
                    className="text-xs font-medium text-[#1F2937] mb-1.5 block"
                  >
                    Contact Name <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="rfp-contact-name"
                    data-testid="rfp-field-contact-name"
                    placeholder="e.g. Sarah Johnson"
                    value={form.contact_name}
                    onChange={(e) => updateField("contact_name", e.target.value)}
                    className={`${INPUT_CLS} ${errors.contact_name ? "border-red-400" : ""}`}
                  />
                  {errors.contact_name && (
                    <p
                      data-testid="rfp-error-contact-name"
                      className="text-xs text-red-500 mt-1.5"
                    >
                      {errors.contact_name}
                    </p>
                  )}
                </div>

                <div>
                  <Label
                    htmlFor="rfp-role"
                    className="text-xs font-medium text-[#1F2937] mb-1.5 block"
                  >
                    Job Title / Role{" "}
                    <span className="text-[#6B7280] font-normal">
                      (optional)
                    </span>
                  </Label>
                  <Input
                    id="rfp-role"
                    data-testid="rfp-field-role"
                    placeholder="e.g. Warehouse Director"
                    value={form.role}
                    onChange={(e) => updateField("role", e.target.value)}
                    className={INPUT_CLS}
                  />
                </div>

                <div>
                  <Label
                    htmlFor="rfp-email"
                    className="text-xs font-medium text-[#1F2937] mb-1.5 block"
                  >
                    Email Address <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="rfp-email"
                    data-testid="rfp-field-email"
                    type="email"
                    placeholder="e.g. sarah@acme.com"
                    value={form.email}
                    onChange={(e) => updateField("email", e.target.value)}
                    className={`${INPUT_CLS} ${errors.email ? "border-red-400" : ""}`}
                  />
                  {errors.email && (
                    <p
                      data-testid="rfp-error-email"
                      className="text-xs text-red-500 mt-1.5"
                    >
                      {errors.email}
                    </p>
                  )}
                </div>

                <div>
                  <Label
                    htmlFor="rfp-phone"
                    className="text-xs font-medium text-[#1F2937] mb-1.5 block"
                  >
                    Phone Number{" "}
                    <span className="text-[#6B7280] font-normal">
                      (optional)
                    </span>
                  </Label>
                  <Input
                    id="rfp-phone"
                    data-testid="rfp-field-phone"
                    type="tel"
                    placeholder="e.g. +49 170 1234567"
                    value={form.phone}
                    onChange={(e) => updateField("phone", e.target.value)}
                    className={INPUT_CLS}
                  />
                </div>

                <div>
                  <Label
                    htmlFor="rfp-industry"
                    className="text-xs font-medium text-[#1F2937] mb-1.5 block"
                  >
                    Industry{" "}
                    <span className="text-[#6B7280] font-normal">
                      (optional)
                    </span>
                  </Label>
                  <Input
                    id="rfp-industry"
                    data-testid="rfp-field-industry"
                    placeholder="e.g. Pharmaceuticals, Retail, Automotive"
                    value={form.industry}
                    onChange={(e) => updateField("industry", e.target.value)}
                    className={INPUT_CLS}
                  />
                </div>

                <div>
                  <Label
                    htmlFor="rfp-country"
                    className="text-xs font-medium text-[#1F2937] mb-1.5 block"
                  >
                    Country / Region{" "}
                    <span className="text-[#6B7280] font-normal">
                      (optional)
                    </span>
                  </Label>
                  <Input
                    id="rfp-country"
                    data-testid="rfp-field-country"
                    placeholder="e.g. Germany, EMEA, APAC"
                    value={form.country}
                    onChange={(e) => updateField("country", e.target.value)}
                    className={INPUT_CLS}
                  />
                </div>

                <div className="sm:col-span-2">
                  <Label
                    htmlFor="rfp-notes"
                    className="text-xs font-medium text-[#1F2937] mb-1.5 block"
                  >
                    Additional Notes / Context{" "}
                    <span className="text-[#6B7280] font-normal">
                      (optional)
                    </span>
                  </Label>
                  <Textarea
                    id="rfp-notes"
                    data-testid="rfp-field-notes"
                    placeholder="Example: Looking for EWM decentralized rollout across 3 DCs with RF modernization and automation integration."
                    value={form.notes}
                    onChange={(e) => updateField("notes", e.target.value)}
                    rows={4}
                    className="rounded-sm text-sm border-[#D1D5DB] text-[#1F2937] placeholder:text-[#9CA3AF] focus:border-[#025979] focus:ring-[#025979] transition-colors"
                  />
                </div>
              </div>
            </div>

            <div className="border-t border-[#E5E7EB]"></div>

            {/* Info messages */}
            <div className="p-6 sm:p-8 space-y-3">
              <div
                data-testid="rfp-priva-info"
                className="flex items-start gap-2.5 bg-[#F8FAFC] border border-[#E5E7EB] rounded-sm px-4 py-3"
              >
                <Info
                  className="w-4 h-4 text-[#6D28D9] mt-0.5 shrink-0"
                  strokeWidth={1.5}
                />
                <span className="text-sm text-[#4B5563] leading-relaxed">
                  PRIVA will use your uploaded documents and business context to
                  prepare SmartBid requirements analysis, scope insights, risks,
                  assumptions, and proposal recommendations.
                </span>
              </div>

              <div
                data-testid="rfp-privacy-info"
                className="flex items-start gap-2.5 bg-[#F8FAFC] border border-[#E5E7EB] rounded-sm px-4 py-3"
              >
                <Lock
                  className="w-4 h-4 text-[#025979] mt-0.5 shrink-0"
                  strokeWidth={1.5}
                />
                <span className="text-sm text-[#4B5563] leading-relaxed">
                  Your uploaded documents are handled securely and are only
                  available inside the authorized SmartBid workflow.
                </span>
              </div>
            </div>

            <div className="border-t border-[#E5E7EB]"></div>

            {/* Footer actions */}
            <div className="p-6 sm:p-8 flex items-center justify-end gap-3">
              <Button
                data-testid="rfp-client-info-continue-btn"
                onClick={handleContinue}
                className="bg-[#025979] hover:bg-[#03738C] text-white h-11 px-8 text-sm font-semibold rounded-sm transition-colors shadow-sm hover:shadow"
              >
                Continue to Document Upload
                <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
